'use client';

import { useEffect, useState } from 'react';

// Default breakpoints from Panda CSS
const breakpoints = {
  base: '0em',
  sm: '30em',
  md: '48em',
  lg: '62em',
  xl: '80em',
  '2xl': '96em',
};

// Convert em to px for easier comparison
const emToPx = (em: string) => {
  return parseFloat(em) * 16;
};

// Get the current breakpoint based on window width
const getCurrentBreakpoint = () => {
  if (typeof window === 'undefined') return 'base';

  const width = window.innerWidth;

  if (width >= emToPx(breakpoints['2xl'])) return '2xl';
  if (width >= emToPx(breakpoints.xl)) return 'xl';
  if (width >= emToPx(breakpoints.lg)) return 'lg';
  if (width >= emToPx(breakpoints.md)) return 'md';
  if (width >= emToPx(breakpoints.sm)) return 'sm';
  return 'base';
};

type BreakpointValue<T> = {
  base?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
  '2xl'?: T;
};

// Helper to resolve responsive value for a given breakpoint
const resolveResponsiveValue = <T>(
  value: T | BreakpointValue<T>,
  currentBreakpoint: string,
): T => {
  // If value is not an object, return it as-is
  if (typeof value !== 'object' || value === null) {
    return value as T;
  }

  const breakpointValues = value as BreakpointValue<T>;

  // Define breakpoint hierarchy (from smallest to largest)
  const breakpointOrder = ['base', 'sm', 'md', 'lg', 'xl', '2xl'];
  const currentIndex = breakpointOrder.indexOf(currentBreakpoint);

  // Look for the value at current breakpoint or closest smaller breakpoint
  for (let i = currentIndex; i >= 0; i--) {
    const bp = breakpointOrder[i];
    if (breakpointValues[bp as keyof BreakpointValue<T>] !== undefined) {
      return breakpointValues[bp as keyof BreakpointValue<T>] as T;
    }
  }

  // Fallback to base or first available value
  return breakpointValues.base || (Object.values(breakpointValues)[0] as T);
};

/**
 * Hook to get responsive values based on current breakpoint
 * @param value - Either a static value or an object with breakpoint keys
 * @returns The appropriate value for the current breakpoint
 */
export function useBreakpointValue<T>(value: T | BreakpointValue<T>): T {
  // Start with base breakpoint to prevent hydration mismatch
  const [currentBreakpoint, setCurrentBreakpoint] = useState<string>('base');
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Mark as client-side and get actual breakpoint
    setIsClient(true);
    const handleResize = () => {
      setCurrentBreakpoint(getCurrentBreakpoint());
    };

    // Set initial breakpoint
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On server and initial client render, always use base breakpoint to prevent hydration mismatch
  const effectiveBreakpoint = isClient ? currentBreakpoint : 'base';

  return resolveResponsiveValue(value, effectiveBreakpoint);
}
